import { createFileRoute } from "@tanstack/react-router";
import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { orpc } from "~/lib/orpc-client";
import { Skeleton } from "@alchema/ui";
import { EmptyState } from "~/components/shared/EmptyState";
import * as m from "~/paraglide/messages";

export const Route = createFileRoute("/analytics/activity")({
  component: ActivityPage,
});

function ActivityEmptyIcon() {
  return (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  );
}

function ActivityPage() {
  const [period, setPeriod] = React.useState<"week" | "month">("week");

  const activityQuery = useQuery(
    orpc.analytics.activitySummary.queryOptions({ input: { period } }),
  );

  const hasData = activityQuery.data && activityQuery.data.daily.length > 0;

  return (
    <div className="space-y-8">
      {/* Period toggle */}
      <div className="flex gap-1 p-0.5 rounded-lg w-fit" style={{ background: "var(--surface-raised)" }}>
        {(["week", "month"] as const).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPeriod(p)}
            className="rounded-md px-4 py-1.5 text-xs font-medium transition-all duration-150"
            style={{
              background: period === p ? "var(--surface)" : "transparent",
              color: period === p ? "var(--text)" : "var(--text-muted)",
              boxShadow: period === p ? "var(--shadow-sm)" : "none",
            }}
          >
            {p === "week" ? m.analytics_activity_period_week() : m.analytics_activity_period_month()}
          </button>
        ))}
      </div>

      {/* Summary stat row */}
      {activityQuery.isLoading ? (
        <div className="grid grid-cols-3 gap-3">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
        </div>
      ) : activityQuery.data ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <StatBlock
            label={m.analytics_activity_total_applied()}
            value={String(activityQuery.data.totalApplied)}
          />
          <StatBlock
            label={m.analytics_activity_total_responses()}
            value={String(activityQuery.data.totalResponses)}
          />
          <StatBlock
            label={m.analytics_activity_avg_response_time()}
            value={activityQuery.data.avgResponseDays != null
              ? `${activityQuery.data.avgResponseDays}d`
              : "--"}
          />
        </div>
      ) : null}

      {/* Activity chart */}
      <div>
        <p className="section-label mb-1">{m.analytics_activity_title()}</p>
        <p className="text-xs mb-4" style={{ color: "var(--text-muted)" }}>
          {m.analytics_activity_desc()}
        </p>

        {activityQuery.isLoading ? (
          <div
            className="rounded-xl flex items-end gap-1 px-5 py-4"
            style={{ border: "1px solid var(--border-subtle)", background: "var(--surface)", height: 200 }}
          >
            {Array.from({ length: period === "week" ? 7 : 15 }).map((_, i) => (
              <Skeleton
                key={i}
                className="flex-1 rounded-t"
                style={{ height: Math.random() * 120 + 20 }}
              />
            ))}
          </div>
        ) : hasData ? (
          <div
            className="rounded-xl px-5 pt-4 pb-3"
            style={{ border: "1px solid var(--border-subtle)", background: "var(--surface)" }}
          >
            {(() => {
              const daily = activityQuery.data!.daily;
              const maxCount = Math.max(...daily.map((d) => d.count), 1);
              const chartHeight = 160;

              return (
                <div className="flex items-end gap-1" style={{ height: chartHeight + 24 }}>
                  {daily.map((day, idx) => {
                    const barHeight = (day.count / maxCount) * chartHeight;
                    const dateLabel = new Date(day.date).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    });
                    const showLabel = period === "week" || idx % 3 === 0;
                    return (
                      <div
                        key={day.date}
                        className="group relative flex flex-1 flex-col items-center"
                        style={{ height: chartHeight + 24 }}
                      >
                        {/* Tooltip */}
                        <div
                          className="pointer-events-none absolute -top-7 hidden rounded px-2 py-1 text-xs font-medium whitespace-nowrap group-hover:block"
                          style={{ background: "var(--text)", color: "var(--text-inverse)" }}
                        >
                          {day.count} app{day.count !== 1 ? "s" : ""}
                        </div>
                        {/* Bar */}
                        <div className="flex flex-1 items-end w-full">
                          <div
                            className="w-full rounded-t transition-all duration-300 hover:opacity-80"
                            style={{
                              height: Math.max(barHeight, day.count > 0 ? 4 : 0),
                              background: "var(--accent)",
                              opacity: 0.75,
                            }}
                          />
                        </div>
                        {/* Date label */}
                        {showLabel && (
                          <span
                            className="mt-1 text-[10px] tabular-nums"
                            style={{ color: "var(--text-muted)" }}
                          >
                            {dateLabel}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        ) : (
          <div
            className="rounded-xl"
            style={{ border: "1px solid var(--border-subtle)", background: "var(--surface)" }}
          >
            <EmptyState
              icon={<ActivityEmptyIcon />}
              title={m.analytics_activity_empty()}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function StatBlock({ label, value }: { label: string; value: string }) {
  return (
    <div
      className="rounded-xl px-5 py-4"
      style={{ border: "1px solid var(--border-subtle)", background: "var(--surface)" }}
    >
      <p className="section-label mb-1">{label}</p>
      <p
        className="text-2xl font-bold tabular-nums tracking-tight"
        style={{ color: "var(--text)", fontFamily: "var(--font-display)" }}
      >
        {value}
      </p>
    </div>
  );
}
